import {
  getAssetActions,
  getAssetEntries,
  type AssetEntry,
  type AssetRegistration,
} from "../../stores/assetStore";
import { getSceneActions } from "../../stores/sceneStore";
import type { Mesh } from "../../types/scene";
import type { ParsedTexture } from "../../core/mjcfParser";
import { logger } from "../logger";
import * as idb from "../idb";
import { createFallbackBlobUrl, hashFile } from "./blobCache";
import { basenameOfPath, createSceneFileResolver, normalizeAssetPath } from "./assetResolver";
import type {
  ParsedBodyMap,
  ParsedMeshMap,
  ParsedTextureRecords,
  SceneAssetIssue,
  SceneAssetProcessingResult,
  SceneAssetValidationReport,
  SceneFileMap,
} from "./types";

/**
 * Process assets (meshes and textures) through the asset store.
 * Returns texture records for use in the scene store.
 */
export async function processSceneAssets(
  parsedBodiesMap: ParsedBodyMap | undefined,
  parsedMeshes: ParsedMeshMap | undefined,
  parsedTextures: Record<string, ParsedTexture | string> | undefined,
  fileMap: SceneFileMap,
  strictAssetValidation = false,
  /** When true, collision mesh OBJs hash after first paint (legacy perf tweak). False keeps fingerprints in sync for processBodyFromStore. */
  deferCollisionMeshProcessing = false,
  meshLibraryPathPrefix?: string
): Promise<SceneAssetProcessingResult> {
  const meshOriginSources =
    typeof meshLibraryPathPrefix === "string" && meshLibraryPathPrefix.trim().length > 0
      ? (() => {
          const n = meshLibraryPathPrefix.trim();
          const withLead = n.startsWith("/") ? n : `/${n}`;
          return { libraryPathPrefix: withLead.endsWith("/") ? withLead : `${withLead}/` };
        })()
      : {};
  const textureRecords: ParsedTextureRecords = {};
  const issues: SceneAssetIssue[] = [];
  const referencedMeshUuids = new Set<string>();
  const unresolvedReferencedMeshesByUuid = new Map<
    string,
    {
      meshUuid: string;
      requestedPath: string;
      normalizedPath: string;
      candidateCount: number;
      candidates: string[];
    }
  >();

  // Shared helper: hash a batch of resolved meshes in parallel, write to IDB in one
  // transaction, fingerprint parsedMeshes in-place, and push entries + meshNameToId.
  type ResolvedMesh = { meshUuid: string; meshPath: string; file: File };
  type HashOutcome =
    | { ok: true; meshUuid: string; meshPath: string; file: File; hex: string }
    | { ok: false; meshUuid: string; meshPath: string; file: File };

  const processMeshBatch = async (
    batch: ResolvedMesh[],
    meshNameToId: Record<string, string>,
    entries: AssetRegistration[]
  ) => {
    if (batch.length === 0) return;
    const outcomes: HashOutcome[] = await Promise.all(
      batch.map(async ({ meshUuid, meshPath, file }) => {
        try {
          const hex = await hashFile(file);
          return { ok: true as const, meshUuid, meshPath, file, hex };
        } catch {
          return { ok: false as const, meshUuid, meshPath, file };
        }
      })
    );
    const succeeded = outcomes.filter((o): o is Extract<HashOutcome, { ok: true }> => o.ok);
    const failed = outcomes.filter((o): o is Extract<HashOutcome, { ok: false }> => !o.ok);

    let idbOk = false;
    if (succeeded.length > 0) {
      try {
        await idb.putBytesMany(succeeded.map((o) => ({ key: o.hex, value: o.file })));
        idbOk = true;
      } catch {
        idbOk = false;
      }
    }
    for (const o of succeeded) {
      const assetId = `mesh:sha256:${o.hex}`;
      if (idbOk) {
        entries.push({
          id: assetId,
          kind: "mesh",
          sources: { idbKey: o.hex, ...meshOriginSources },
          name: o.meshPath,
          status: "idle",
          bytes: o.file.size,
          hash: o.hex,
        });
      } else {
        const url = createFallbackBlobUrl(o.file);
        entries.push({
          id: assetId,
          kind: "mesh",
          sources: { url, ...meshOriginSources },
          name: o.meshPath,
          status: "idle",
          bytes: o.file.size,
          hash: o.hex,
        });
      }
      meshNameToId[o.meshUuid] = assetId;
    }
    for (const o of failed) {
      const url = createFallbackBlobUrl(o.file);
      const assetId = `mesh:url:${url}`;
      entries.push({
        id: assetId,
        kind: "mesh",
        sources: { url, ...meshOriginSources },
        name: o.meshPath,
        status: "idle",
        bytes: o.file.size,
      });
      meshNameToId[o.meshUuid] = assetId;
    }
  };

  const applyFingerprints = (
    meshNameToId: Record<string, string>,
    defs: ParsedMeshMap | undefined
  ) => {
    if (!defs) return;
    Object.entries(defs).forEach(([name, meshDef]) => {
      if (!meshDef) return;
      const assetId = meshNameToId[name];
      if (assetId) meshDef.fingerprint = assetId;
    });
  };

  const seedMeshIdsFromRegisteredAssets = (
    meshNameToId: Record<string, string>,
    defs: ParsedMeshMap | undefined,
    resolver: ReturnType<typeof createSceneFileResolver>
  ) => {
    if (!defs) return;
    const entries = Object.values(getAssetEntries() || {}).filter(
      (entry): entry is AssetEntry => entry?.kind === "mesh" && typeof entry?.id === "string"
    );
    if (entries.length === 0) return;

    const directExactPathMap = new Map<string, string[]>();
    const aliasExactPathMap = new Map<string, string[]>();
    const directBasenameMap = new Map<string, string[]>();
    const aliasBasenameMap = new Map<string, string[]>();
    const entryById = new Map<string, AssetEntry>();
    const addCandidate = (candidate: string, entryId: string, isDirect: boolean) => {
      const normalized = normalizeAssetPath(candidate);
      if (!normalized) return;
      const exactMap = isDirect ? directExactPathMap : aliasExactPathMap;
      const exactList = exactMap.get(normalized) || [];
      exactList.push(entryId);
      exactMap.set(normalized, exactList);

      const baseMap = isDirect ? directBasenameMap : aliasBasenameMap;
      const base = basenameOfPath(normalized);
      const baseList = baseMap.get(base) || [];
      baseList.push(entryId);
      baseMap.set(base, baseList);
    };
    entries.forEach((entry) => {
      entryById.set(entry.id, entry);
      const entryName = typeof entry?.name === "string" ? entry.name : "";
      const libraryPrefix =
        typeof entry?.sources?.libraryPathPrefix === "string"
          ? entry.sources.libraryPathPrefix
          : "";
      const directCandidates = [entryName];
      const aliasCandidates: string[] = [];
      if (entryName && !entryName.includes("/")) {
        aliasCandidates.push(`assets/${entryName}`);
      }
      if (entryName && libraryPrefix) {
        const prefix = libraryPrefix.replace(/\/+$/, "");
        const normalizedEntryName = entryName.replace(/^\/+/, "");
        aliasCandidates.push(`${prefix}/${normalizedEntryName}`);
        if (!normalizedEntryName.includes("/")) {
          aliasCandidates.push(`${prefix}/assets/${normalizedEntryName}`);
        }
      }
      directCandidates.forEach((candidate) => addCandidate(candidate, entry.id, true));
      aliasCandidates.forEach((candidate) => addCandidate(candidate, entry.id, false));
    });

    const uniqueIds = (ids: string[]) => Array.from(new Set(ids));
    const isCatalogAssetPath = (normalizedPath: string) =>
      normalizedPath.startsWith("robots/") || normalizedPath.startsWith("objects/");
    const preferLatestCatalogAsset = (ids: string[], normalizedPath: string) => {
      if (ids.length <= 1 || !isCatalogAssetPath(normalizedPath)) return ids;
      const sorted = [...ids].sort((a, b) => {
        const aEntry = entryById.get(a);
        const bEntry = entryById.get(b);
        const aTime = aEntry?.updatedAt ?? aEntry?.createdAt ?? 0;
        const bTime = bEntry?.updatedAt ?? bEntry?.createdAt ?? 0;
        if (aTime !== bTime) return bTime - aTime;
        return b.localeCompare(a);
      });
      return sorted[0] ? [sorted[0]] : ids;
    };
    const activeLibraryPrefix = normalizeAssetPath(meshLibraryPathPrefix || "");
    const preferCurrentLibraryPrefix = (ids: string[]) => {
      const unique = uniqueIds(ids);
      if (unique.length <= 1 || !activeLibraryPrefix) return unique;
      const preferred = unique.filter((id) => {
        const entry = entryById.get(id);
        const entryPrefix = normalizeAssetPath(entry?.sources?.libraryPathPrefix || "");
        return entryPrefix === activeLibraryPrefix;
      });
      return preferred.length > 0 ? preferred : unique;
    };
    const collapseEquivalentAssetIds = (ids: string[]) => {
      const unique = preferCurrentLibraryPrefix(ids);
      if (unique.length <= 1) return unique;
      const sourceGroups = new Map<string, string[]>();
      const unresolved: string[] = [];
      unique.forEach((id) => {
        const entry = entryById.get(id);
        const sources = entry?.sources || {};
        const sourceKey =
          (typeof entry?.hash === "string" && entry.hash ? `hash:${entry.hash}` : "") ||
          (typeof sources.idbKey === "string" && sources.idbKey ? `idb:${sources.idbKey}` : "") ||
          (typeof sources.url === "string" && sources.url ? `url:${sources.url}` : "") ||
          (typeof sources.blobUrl === "string" && sources.blobUrl ? `blob:${sources.blobUrl}` : "");
        if (!sourceKey) {
          unresolved.push(id);
          return;
        }
        const group = sourceGroups.get(sourceKey) || [];
        group.push(id);
        sourceGroups.set(sourceKey, group);
      });
      if (sourceGroups.size === 1 && unresolved.length === 0) {
        const [firstGroup] = sourceGroups.values();
        return firstGroup?.[0] ? [firstGroup[0]] : unique;
      }
      return unique;
    };
    const getExactCandidates = (normalizedPath: string) => {
      const directCandidates = [...(directExactPathMap.get(normalizedPath) || [])];
      if (!normalizedPath.includes("/")) {
        directCandidates.push(...(directExactPathMap.get(`assets/${normalizedPath}`) || []));
      }
      const collapsedDirect = collapseEquivalentAssetIds(directCandidates);
      if (collapsedDirect.length > 0) {
        return preferLatestCatalogAsset(collapsedDirect, normalizedPath);
      }

      const candidates = [...(aliasExactPathMap.get(normalizedPath) || [])];
      if (!normalizedPath.includes("/")) {
        candidates.push(...(aliasExactPathMap.get(`assets/${normalizedPath}`) || []));
      }
      return preferLatestCatalogAsset(collapseEquivalentAssetIds(candidates), normalizedPath);
    };
    const getBasenameCandidates = (normalizedPath: string, base: string) => {
      const direct = collapseEquivalentAssetIds(directBasenameMap.get(base) || []);
      if (direct.length > 0) return preferLatestCatalogAsset(direct, normalizedPath);
      return preferLatestCatalogAsset(
        collapseEquivalentAssetIds(aliasBasenameMap.get(base) || []),
        normalizedPath
      );
    };

    Object.entries(defs).forEach(([meshUuid, meshDef]) => {
      if (!meshDef) return;
      if (meshNameToId[meshUuid]) return;
      const meshPath = typeof meshDef.url === "string" ? meshDef.url : "";
      const normalizedPath = normalizeAssetPath(meshPath);
      if (!normalizedPath) return;
      if (resolver.hasExactFile(meshPath)) return;
      if (typeof meshDef.fingerprint === "string" && meshDef.fingerprint.length > 0) {
        meshNameToId[meshUuid] = meshDef.fingerprint;
        return;
      }

      const exactCandidates = getExactCandidates(normalizedPath);
      if (exactCandidates.length === 1) {
        meshNameToId[meshUuid] = exactCandidates[0];
        return;
      }
      if (exactCandidates.length > 1) {
        logger.warn("[sceneLoader] Ambiguous cached mesh match by exact path", {
          meshUuid,
          meshPath,
          candidateCount: exactCandidates.length,
        });
        return;
      }

      const base = basenameOfPath(normalizedPath);
      const basenameCandidates = getBasenameCandidates(normalizedPath, base);
      if (basenameCandidates.length === 1) {
        meshNameToId[meshUuid] = basenameCandidates[0];
        return;
      }
      if (basenameCandidates.length > 1) {
        logger.warn("[sceneLoader] Ambiguous cached mesh match by basename", {
          meshUuid,
          meshPath,
          basename: base,
          candidateCount: basenameCandidates.length,
        });
      }
    });
  };

  // Resolve a set of mesh UUIDs against the file map. Pushes issues/unresolved as a side effect.
  const resolveMeshSet = (
    uuids: Set<string>,
    meshDefinitions: ParsedMeshMap,
    resolver: ReturnType<typeof createSceneFileResolver>,
    targetIssues: SceneAssetIssue[],
    targetUnresolved: Map<string, SceneAssetValidationReport["unresolvedReferencedMeshes"][number]>
  ): ResolvedMesh[] => {
    const resolved: ResolvedMesh[] = [];
    for (const meshUuid of uuids) {
      const meshMeta = meshDefinitions?.[meshUuid];
      if (!meshMeta) {
        targetIssues.push({ kind: "mesh", reason: "missingMeshDef", meshUuid: String(meshUuid) });
        continue;
      }
      const meshPath = meshMeta?.url;
      if (!meshPath) {
        targetIssues.push({ kind: "mesh", reason: "missingPath", meshUuid: String(meshUuid) });
        continue;
      }
      const r = resolver.resolve(meshPath);
      if (!r.file) {
        const reason = r.isAmbiguous ? "ambiguousPath" : "unresolvedPath";
        const entry = {
          meshUuid: String(meshUuid),
          requestedPath: meshPath,
          normalizedPath: r.normalizedPath,
          candidateCount: r.candidates.length,
          candidates: r.candidates.slice(0, 10),
        };
        targetIssues.push({ kind: "mesh", reason, ...entry });
        targetUnresolved.set(String(meshUuid), entry);
        continue;
      }
      resolved.push({ meshUuid, meshPath, file: r.file });
    }
    return resolved;
  };

  let backgroundWork: () => Promise<void> = async () => {};

  try {
    const { registerAssets } = getAssetActions();
    const meshDefinitions = parsedMeshes ?? {};
    const visualEntries: AssetRegistration[] = [];
    const meshNameToId: Record<string, string> = {};
    const texNameToId: Record<string, string> = {};
    const resolver = createSceneFileResolver(fileMap);

    // Reuse already-registered mesh assets when an uploaded XML references files
    // that were not re-uploaded in this file batch.
    seedMeshIdsFromRegisteredAssets(meshNameToId, parsedMeshes, resolver);

    // Classify geom mesh references as visual (visualOnly === true) or collision.
    const visualMeshUuids = new Set<string>();
    const collisionMeshUuids = new Set<string>();
    Object.values(parsedBodiesMap || {}).forEach((node) => {
      const geoms = Array.isArray(node?.geoms) ? node.geoms : [];
      geoms.forEach((geom) => {
        if (geom && typeof geom.mesh === "string") {
          // Only defer explicitly-collision geoms (visualOnly === false).
          // visualOnly === true and visualOnly === undefined both go to the visual queue.
          if (geom.visualOnly === false) {
            collisionMeshUuids.add(geom.mesh);
          } else {
            visualMeshUuids.add(geom.mesh);
          }
        }
      });
    });
    // A mesh shared by both visual and collision geoms is treated as visual (process eagerly).
    collisionMeshUuids.forEach((uuid) => {
      if (visualMeshUuids.has(uuid)) collisionMeshUuids.delete(uuid);
    });
    visualMeshUuids.forEach((uuid) => referencedMeshUuids.add(uuid));
    collisionMeshUuids.forEach((uuid) => referencedMeshUuids.add(uuid));
    const visualMeshUuidsNeedingFile = new Set(
      Array.from(visualMeshUuids).filter((uuid) => !meshNameToId[uuid])
    );
    const collisionMeshUuidsNeedingFile = new Set(
      Array.from(collisionMeshUuids).filter((uuid) => !meshNameToId[uuid])
    );

    // Resolve visual mesh files (issues tracked in main report).
    const resolvedVisual = resolveMeshSet(
      visualMeshUuidsNeedingFile,
      meshDefinitions,
      resolver,
      issues,
      unresolvedReferencedMeshesByUuid
    );
    // Resolve collision mesh files.
    const collisionIssues: SceneAssetIssue[] = [];
    const collisionUnresolved = new Map<
      string,
      SceneAssetValidationReport["unresolvedReferencedMeshes"][number]
    >();
    const resolvedCollision = resolveMeshSet(
      collisionMeshUuidsNeedingFile,
      meshDefinitions,
      resolver,
      collisionIssues,
      collisionUnresolved
    );

    // --- Main (awaited) phase: visual meshes + textures ---
    await processMeshBatch(resolvedVisual, meshNameToId, visualEntries);
    if (!deferCollisionMeshProcessing) {
      await processMeshBatch(resolvedCollision, meshNameToId, visualEntries);
      if (collisionIssues.length > 0) {
        issues.push(...collisionIssues);
      }
      if (collisionUnresolved.size > 0) {
        collisionUnresolved.forEach((entry, meshUuid) => {
          unresolvedReferencedMeshesByUuid.set(meshUuid, entry);
        });
      }
    }
    applyFingerprints(meshNameToId, parsedMeshes);

    if (parsedTextures) {
      // 1. Resolve all texture files synchronously; push unresolvable entries to issues.
      type ResolvedTexture = { textureName: string; file: File };
      const resolvedTextures: ResolvedTexture[] = [];
      for (const [textureName, texInfo] of Object.entries(parsedTextures)) {
        const relativePath = typeof texInfo === "string" ? texInfo : texInfo?.file;
        const resolved = resolver.resolve(relativePath);
        const file = resolved.file;
        if (!file) {
          issues.push({
            kind: "texture",
            reason: resolved.isAmbiguous ? "ambiguousPath" : "unresolvedPath",
            textureName,
            requestedPath: relativePath || "",
            normalizedPath: resolved.normalizedPath,
            candidateCount: resolved.candidates.length,
            candidates: resolved.candidates.slice(0, 10),
          });
          continue;
        }
        resolvedTextures.push({ textureName, file });
      }

      // 2. Hash all resolved textures in parallel.
      type TexHashOutcome =
        | { ok: true; textureName: string; file: File; hex: string }
        | { ok: false; textureName: string; file: File };
      const texOutcomes: TexHashOutcome[] = await Promise.all(
        resolvedTextures.map(async ({ textureName, file }) => {
          try {
            const hex = await hashFile(file);
            return { ok: true as const, textureName, file, hex };
          } catch {
            return { ok: false as const, textureName, file };
          }
        })
      );
      const texSucceeded = texOutcomes.filter(
        (o): o is Extract<TexHashOutcome, { ok: true }> => o.ok
      );
      const texFailed = texOutcomes.filter(
        (o): o is Extract<TexHashOutcome, { ok: false }> => !o.ok
      );

      // 3. Batch write all hashed textures to IDB in a single transaction.
      let texIdbOk = false;
      if (texSucceeded.length > 0) {
        try {
          await idb.putBytesMany(texSucceeded.map((o) => ({ key: o.hex, value: o.file })));
          texIdbOk = true;
        } catch {
          texIdbOk = false;
        }
      }

      // 4. Build asset entries for successfully hashed textures.
      for (const o of texSucceeded) {
        const id = `tex:sha256:${o.hex}`;
        texNameToId[o.textureName] = id;
        const baseEntry = {
          id,
          kind: "texture" as const,
          name: o.textureName,
          status: "idle" as const,
          bytes: o.file.size,
          hash: o.hex,
        };
        if (texIdbOk) {
          visualEntries.push({ ...baseEntry, sources: { idbKey: o.hex } });
        } else {
          const url = createFallbackBlobUrl(o.file);
          visualEntries.push({ ...baseEntry, sources: { url } });
        }
      }
      // Fall back to blob URLs for textures that failed to hash.
      for (const o of texFailed) {
        const url = createFallbackBlobUrl(o.file);
        const id = `tex:url:${url}`;
        texNameToId[o.textureName] = id;
        visualEntries.push({
          id,
          kind: "texture",
          sources: { url },
          name: o.textureName,
          status: "idle",
          bytes: o.file.size,
        });
      }

      Object.entries(parsedTextures).forEach(([textureName, texInfo]) => {
        const relativePath = typeof texInfo === "string" ? texInfo : texInfo?.file;
        const fingerprint = texNameToId[textureName];
        const assetUrl = typeof relativePath === "string" ? relativePath : undefined;
        textureRecords[textureName] = {
          name: textureName,
          kind: typeof texInfo === "object" ? texInfo.kind : undefined,
          fingerprint,
          url: assetUrl,
          width: typeof texInfo === "object" ? texInfo.width : undefined,
          height: typeof texInfo === "object" ? texInfo.height : undefined,
          channels: typeof texInfo === "object" ? texInfo.nchannel : undefined,
        };
      });
    }

    // Register visual assets + textures immediately so the scene can build.
    if (visualEntries.length > 0 && typeof registerAssets === "function") {
      registerAssets(visualEntries);
      try {
        if (typeof idb.saveManifest === "function" && typeof getAssetEntries === "function") {
          const allEntries = Object.values(getAssetEntries() || {});
          const persistable = allEntries.filter((e) => {
            const s = e?.sources;
            if (!s) return true;
            return s.idbKey || s.blobUrl || !s.url?.startsWith("blob:");
          });
          await idb.saveManifest(persistable);
        }
      } catch {
        // ignore manifest persistence errors
      }
    }

    // --- Deferred phase: collision meshes (fire-and-forget after scene build) ---
    backgroundWork = async () => {
      if (!deferCollisionMeshProcessing) return;
      if (resolvedCollision.length === 0) return;
      try {
        const collisionEntries: AssetRegistration[] = [];
        const collisionMeshNameToId: Record<string, string> = {};
        await processMeshBatch(resolvedCollision, collisionMeshNameToId, collisionEntries);
        applyFingerprints(collisionMeshNameToId, parsedMeshes);

        const { registerAssets: registerCollision } = getAssetActions();
        if (collisionEntries.length > 0 && typeof registerCollision === "function") {
          registerCollision(collisionEntries);
          try {
            if (typeof idb.saveManifest === "function" && typeof getAssetEntries === "function") {
              await idb.saveManifest(Object.values(getAssetEntries() || {}));
            }
          } catch {
            // ignore manifest persistence errors
          }
        }
        // Push collision fingerprints into the store so future scene operations can use them.
        if (parsedMeshes) {
          getSceneActions().upsertMeshes(parsedMeshes as unknown as Record<string, Partial<Mesh>>);
        }
        if (collisionUnresolved.size > 0) {
          logger.warn("[sceneLoader] Collision mesh resolution issues (non-blocking)", {
            unresolvedCount: collisionUnresolved.size,
            sample: Array.from(collisionUnresolved.values()).slice(0, 5),
          });
        }
      } catch (err) {
        logger.warn("[sceneLoader] Background collision mesh processing failed:", err);
      }
    };
  } catch (error) {
    logger.warn("[sceneLoader] Skipping asset processing due to error:", error);
  }

  referencedMeshUuids.forEach((meshUuid) => {
    const meshDef = parsedMeshes?.[meshUuid];
    if (meshDef?.fingerprint) return;

    const requestedPath =
      typeof meshDef?.url === "string" && meshDef.url.trim() ? meshDef.url : String(meshUuid);
    const existingIssue = issues.some(
      (issue) => issue.kind === "mesh" && issue.meshUuid === String(meshUuid)
    );
    if (!existingIssue) {
      issues.push({
        kind: "mesh",
        reason: meshDef ? "missingPath" : "missingMeshDef",
        meshUuid: String(meshUuid),
        requestedPath,
        normalizedPath: normalizeAssetPath(requestedPath),
        candidateCount: 0,
        candidates: [],
      });
    }
    if (!unresolvedReferencedMeshesByUuid.has(String(meshUuid))) {
      unresolvedReferencedMeshesByUuid.set(String(meshUuid), {
        meshUuid: String(meshUuid),
        requestedPath,
        normalizedPath: normalizeAssetPath(requestedPath),
        candidateCount: 0,
        candidates: [],
      });
    }
  });

  const unresolvedReferencedMeshes = Array.from(unresolvedReferencedMeshesByUuid.values());
  const meshIssueCount = issues.filter((issue) => issue.kind === "mesh").length;
  const textureIssueCount = issues.filter((issue) => issue.kind === "texture").length;
  const validation: SceneAssetValidationReport = {
    issues,
    unresolvedReferencedMeshes,
    summary: {
      meshIssueCount,
      textureIssueCount,
      unresolvedReferencedMeshCount: unresolvedReferencedMeshes.length,
    },
  };

  if (unresolvedReferencedMeshes.length > 0) {
    const payload = {
      unresolvedCount: unresolvedReferencedMeshes.length,
      sample: unresolvedReferencedMeshes.slice(0, 10),
    };
    if (strictAssetValidation) {
      const err = new Error(
        `[sceneLoader] Asset validation failed: ${unresolvedReferencedMeshes.length} referenced mesh(es) unresolved`
      ) as Error & { details?: unknown };
      err.details = payload;
      throw err;
    }
    logger.warn("[sceneLoader] Asset validation warning (non-blocking)", payload);
  }

  return { textureRecords, validation, backgroundWork };
}
